import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'
import { FigmaCard, FigmaButton } from '../components/FigmaUIElements'
import { Rocket, Wrench, Zap, Shield, Star, Settings } from 'lucide-react'

const Shipyard: React.FC = () => {
  const { settings } = useAnimations()

  return (
    <motion.div
      className="min-h-screen p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-8"
          animate={settings.floatingElements ? {
            y: [0, -5, 0],
            transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
          } : {}}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4">
            Imperial Shipyard
          </h1>
          <p className="text-gray-300 text-lg">
            Design, build, and customize your fleet. The future of your empire depends on your ships.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <FigmaCard className="p-6">
            <div className="text-center">
              <Rocket className="w-12 h-12 text-cyan-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Fighters</h3>
              <p className="text-gray-400 text-sm mb-4">Fast, agile combat vessels</p>
              <FigmaButton variant="primary" className="w-full">
                Build Fighter
              </FigmaButton>
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="text-center">
              <Shield className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Cruisers</h3>
              <p className="text-gray-400 text-sm mb-4">Balanced attack and defense</p>
              <FigmaButton variant="secondary" className="w-full">
                Build Cruiser
              </FigmaButton>
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="text-center">
              <Star className="w-12 h-12 text-purple-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Battleships</h3>
              <p className="text-gray-400 text-sm mb-4">Heavy assault vessels</p>
              <FigmaButton variant="secondary" className="w-full">
                Build Battleship
              </FigmaButton>
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="text-center">
              <Settings className="w-12 h-12 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Support</h3>
              <p className="text-gray-400 text-sm mb-4">Utility and support ships</p>
              <FigmaButton variant="secondary" className="w-full">
                Build Support
              </FigmaButton>
            </div>
          </FigmaCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Construction Bay</h3>
            <div className="space-y-4">
              {[
                { 
                  ship: 'Phoenix-class Destroyer', 
                  progress: 85, 
                  eta: '1h 20m',
                  cost: '45,000 Credits'
                },
                { 
                  ship: 'Viper Squadron (x4)', 
                  progress: 60, 
                  eta: '2h 45m',
                  cost: '28,000 Credits'
                },
                { 
                  ship: 'Atlas Supply Vessel', 
                  progress: 30, 
                  eta: '5h 15m',
                  cost: '32,000 Credits'
                }
              ].map((construction, index) => (
                <motion.div
                  key={construction.ship}
                  className="p-4 bg-gray-800/50 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{construction.ship}</h4>
                    <span className="text-gray-400 text-sm">ETA: {construction.eta}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3 mb-2">
                    <motion.div
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 h-3 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${construction.progress}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{construction.cost}</span>
                    <span className="text-cyan-400">{construction.progress}% Complete</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Ship Design</h3>
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-white mb-3">Weapon Systems</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Plasma Cannon', level: 'III' },
                    { name: 'Ion Torpedo', level: 'II' },
                    { name: 'Laser Array', level: 'IV' },
                    { name: 'Rail Gun', level: 'I' }
                  ].map((weapon) => (
                    <div key={weapon.name} className="p-2 bg-gray-800/30 rounded text-center">
                      <div className="text-white text-sm font-medium">{weapon.name}</div>
                      <div className="text-gray-400 text-xs">Level {weapon.level}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium text-white mb-3">Defense Systems</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Energy Shield', level: 'III' },
                    { name: 'Armor Plating', level: 'II' },
                    { name: 'Point Defense', level: 'IV' },
                    { name: 'Stealth Field', level: 'I' }
                  ].map((defense) => (
                    <div key={defense.name} className="p-2 bg-gray-800/30 rounded text-center">
                      <div className="text-white text-sm font-medium">{defense.name}</div>
                      <div className="text-gray-400 text-xs">Level {defense.level}</div>
                    </div>
                  ))}
                </div>
              </div>

              <FigmaButton variant="primary" className="w-full">
                Launch Ship Designer
              </FigmaButton>
            </div>
          </FigmaCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Shipyard